import React from "react";
import { Card, Stack, Table, Text } from "@mantine/core";
import { WCSTCollectedData, WCSTCardInfo, WCSTTestResult, WCSTTrial } from "types/testTypes";

const formatReactionTime = (ms: number | null | undefined) => {
  if (ms == null) return "—";
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  return `${ms} ms`;
};

const formatSelectionTime = (trial: WCSTTrial): string => {
  const sec = trial.selectionTime;
  if (typeof sec === "number")
    return sec >= 1 ? `${sec.toFixed(2)} s` : `${(sec * 1000).toFixed(0)} ms`;
  return formatReactionTime(trial.reactionTimeMs);
};

const formatCard = (card: WCSTCardInfo | null | undefined): string => {
  if (!card) return "—";
  const parts: string[] = [];
  if (card.color != null && card.color !== "") parts.push(String(card.color));
  else parts.push("—");
  if (card.shape != null) parts.push(String(card.shape));
  else parts.push("—");
  if (card.count != null) parts.push(String(card.count));
  else parts.push("—");
  return parts.join(", ");
};

/** Backend result item may use correctness, ruleType, trialNumber; normalize to WCSTTrial */
function normalizeTrial(raw: WCSTTrial | Record<string, unknown>, index: number): WCSTTrial {
  const hasCorrect = "correct" in raw && typeof (raw as WCSTTrial).correct === "boolean";
  if (hasCorrect) {
    return raw as WCSTTrial;
  }
  const correctness = (raw as Record<string, unknown>)["correctness"];
  const correct = correctness === true;
  const ruleType = (raw as Record<string, unknown>)["ruleType"];
  const rule = typeof ruleType === "string" ? ruleType : undefined;
  const trialNumber = (raw as Record<string, unknown>)["trialNumber"];
  const trialIndex = typeof trialNumber === "number" ? trialNumber : index + 1;
  const selectionTime = (raw as Record<string, unknown>)["selectionTime"];
  const selectionTimeNum = typeof selectionTime === "number" ? selectionTime : undefined;
  const reactionTimeMs = selectionTimeNum != null ? Math.round(selectionTimeNum * 1000) : undefined;
  const parseCard = (obj: unknown): WCSTCardInfo | undefined => {
    if (obj == null || typeof obj !== "object") return undefined;
    const o = obj as Record<string, unknown>;
    const color = typeof o["color"] === "string" ? o["color"] : undefined;
    const shape = typeof o["shape"] === "number" ? o["shape"] : undefined;
    const count = typeof o["count"] === "number" ? o["count"] : undefined;
    if (color === undefined && shape === undefined && count === undefined) return undefined;
    return { color, shape, count };
  };
  const selectedReferenceCard = parseCard(
    (raw as Record<string, unknown>)["selectedReferenceCard"],
  );
  const playerCard = parseCard((raw as Record<string, unknown>)["playerCard"]);
  const controllerUsed =
    typeof (raw as Record<string, unknown>)["controllerUsed"] === "string"
      ? ((raw as Record<string, unknown>)["controllerUsed"] as string)
      : undefined;
  return {
    trialIndex,
    correct,
    rule,
    reactionTimeMs,
    selectionTime: selectionTimeNum,
    selectedReferenceCard,
    playerCard,
    controllerUsed,
  };
}

function computeAverageReactionTimeMs(trials: WCSTTrial[]): number | null {
  const withTime = trials.filter(
    (t) => typeof t.reactionTimeMs === "number" || typeof t.selectionTime === "number",
  );
  if (withTime.length === 0) return null;
  const sum = withTime.reduce((acc, t) => {
    if (typeof t.reactionTimeMs === "number") return acc + t.reactionTimeMs!;
    if (typeof t.selectionTime === "number") return acc + Math.round(t.selectionTime * 1000);
    return acc;
  }, 0);
  return Math.round(sum / withTime.length);
}

function computeAverageTrialsToFindRule(trials: WCSTTrial[]): number | null {
  if (trials.length === 0) return null;
  const counts: number[] = [];
  let segmentStart = 0;
  let currentRule = trials[0].rule;
  for (let i = 1; i <= trials.length; i++) {
    const ruleChanged = i === trials.length || trials[i]?.rule !== currentRule;
    if (ruleChanged) {
      const segment = trials.slice(segmentStart, i);
      const firstCorrectIdx = segment.findIndex((t) => t.correct);
      const trialsToFind = firstCorrectIdx >= 0 ? firstCorrectIdx + 1 : segment.length;
      counts.push(trialsToFind);
      if (i < trials.length && trials[i]) {
        segmentStart = i;
        currentRule = trials[i].rule;
      }
    }
  }
  if (counts.length === 0) return null;
  const avg = counts.reduce((a, b) => a + b, 0) / counts.length;
  return Math.round(avg * 10) / 10;
}

export default function WCSTTestEntry({ entry }: { entry: WCSTTestResult | null }) {
  const rawTrials = entry?.results ?? entry?.wcstData?.trials ?? [];
  const trials = rawTrials.map((t, i) =>
    normalizeTrial(t as WCSTTrial | Record<string, unknown>, i),
  );
  const wcstData: WCSTCollectedData | null | undefined = entry?.wcstData ?? null;
  const averageReactionTimeMs =
    wcstData?.averageReactionTimeMs ?? computeAverageReactionTimeMs(trials) ?? null;
  const averageTrialsToFindRule =
    wcstData?.averageTrialsToFindRule ?? computeAverageTrialsToFindRule(trials) ?? null;

  if (trials.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No WCST results available.
      </Text>
    );
  }

  return (
    <Stack gap="md">
      <Card withBorder>
        <Text fw={600} mb="sm">
          Summary
        </Text>
        <Stack gap="xs">
          <Text size="sm">Average reaction time: {formatReactionTime(averageReactionTimeMs)}</Text>
          <Text size="sm">
            Average trials to find rule:{" "}
            {averageTrialsToFindRule != null ? averageTrialsToFindRule : "—"}
          </Text>
        </Stack>
      </Card>
      <Card withBorder>
        <Text fw={600} mb="sm">
          Trials
        </Text>
        <Table withRowBorders withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Trial</Table.Th>
              <Table.Th>Correct</Table.Th>
              <Table.Th>Rule</Table.Th>
              <Table.Th>Selection time</Table.Th>
              <Table.Th>Reference card</Table.Th>
              <Table.Th>Player card</Table.Th>
              <Table.Th>Controller</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {trials.map((trial, idx) => (
              <Table.Tr key={idx}>
                <Table.Td>{trial.trialIndex ?? idx + 1}</Table.Td>
                <Table.Td>{trial.correct ? "Correct" : "Incorrect"}</Table.Td>
                <Table.Td>{trial.rule ?? "—"}</Table.Td>
                <Table.Td>{formatSelectionTime(trial)}</Table.Td>
                <Table.Td>{formatCard(trial.selectedReferenceCard)}</Table.Td>
                <Table.Td>{formatCard(trial.playerCard)}</Table.Td>
                <Table.Td>{trial.controllerUsed ?? "—"}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
